import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CalendarPlus, Radio } from 'lucide-react';
import type { XkEvent, XkEventGroup } from '@/lib/api/types';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { useT } from '@/lib/i18n/useT';
import { GameLinkPanel } from '@/features/brand/GameLinkPanel';
import type { GameLink } from '@/features/events/defaultJourney';

/**
 * Task 8.5 — what a returning manager sees instead of the quick start.
 *
 * One line of state and one thing to do. The temptation on a home screen is to
 * report everything; the point of this strip is the opposite — it says just
 * enough to orient ("three events, one live, next one Thursday") and then gets
 * out of the way of the action, which stays the visually primary element.
 *
 * It reads the events already loaded by the page rather than fetching its own
 * summary. A recurring home should not cost four extra requests to tell you
 * something you could count.
 */
export function BrandPulse({
  projects,
  events,
  gameLink,
  onAddEvent,
}: {
  projects: XkEventGroup[];
  events: XkEvent[];
  /** The live game's address, when one is wired. */
  gameLink: GameLink | null;
  onAddEvent: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const language = usePrefsStore((s) => s.language);
  const locale = language === 'es' ? 'es-ES' : 'en-US';

  const { liveCount, live, next } = useMemo(() => {
    const now = Date.now();
    const active = events.filter((e) => e.eventStatus === 'Active');
    const isLive = (e: XkEvent) => {
      const s = e.startDate ? new Date(e.startDate).getTime() : null;
      const en = e.endDate ? new Date(e.endDate).getTime() : null;
      return s != null && en != null && now >= s && now <= en;
    };
    const liveOnes = active.filter(isLive);
    const upcoming = active
      .filter((e) => e.startDate && new Date(e.startDate).getTime() > now)
      .sort(
        (a, b) =>
          new Date(a.startDate!).getTime() - new Date(b.startDate!).getTime(),
      );
    return { liveCount: liveOnes.length, live: liveOnes[0] ?? null, next: upcoming[0] ?? null };
  }, [events]);

  const dateLabel = (iso?: string | null) => {
    if (!iso) return '';
    try {
      return new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
      }).format(new Date(iso));
    } catch {
      return '';
    }
  };

  // The one thing to do next, in order of what is most urgent: watch what is
  // running, open what is about to run, or schedule something.
  const action = live
    ? {
        label: t('brandPulse.watchLive'),
        icon: <Radio className="h-4 w-4" />,
        run: () => navigate(`/projects/${live.eventGroupId}/events/${live.id}`),
      }
    : next
      ? {
          label: t('brandPulse.openNext'),
          icon: <ArrowRight className="h-4 w-4" />,
          run: () => navigate(`/projects/${next.eventGroupId}/events/${next.id}`),
        }
      : {
          label: t('brandPulse.scheduleEvent'),
          icon: <CalendarPlus className="h-4 w-4" />,
          run: onAddEvent,
        };

  const facts = [
    `${projects.length} ${t(projects.length === 1 ? 'brandPulse.projectOne' : 'brandPulse.projectMany')}`,
    `${events.length} ${t(events.length === 1 ? 'brandPulse.eventOne' : 'brandPulse.eventMany')}`,
    liveCount > 0 ? `${liveCount} ${t('brandPulse.live')}` : null,
  ].filter(Boolean) as string[];

  return (
    <section className="mt-6 space-y-3 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-bg-surface)] px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[var(--color-xk-text)]">
          {facts.join(' · ')}
        </p>
        <p className="mt-0.5 truncate text-xs text-[var(--color-xk-text-subtle)]">
          {live
            ? t('brandPulse.liveNow', { name: live.name })
            : next
              ? t('brandPulse.nextUp', { name: next.name, when: dateLabel(next.startDate) })
              : t('brandPulse.nothingScheduled')}
        </p>
      </div>

      <button
        type="button"
        onClick={action.run}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-3.5 py-2 text-sm font-semibold text-[var(--color-fg-on-accent)] transition-opacity hover:opacity-90"
      >
        {action.icon}
        {action.label}
      </button>
      </div>

      {/* The address of the running game. Managers come back here for it more
          often than for any number on this strip. */}
      {gameLink && <GameLinkPanel link={gameLink} />}
    </section>
  );
}
